import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  TrendingUp, TrendingDown, Flame, Moon, Clock, 
  Tag, Loader2, RefreshCw, Zap, AlertCircle, Check
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Surge Status Badge - Shows current pricing conditions
export function SurgeStatusBadge({ onPriceInfo }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/dynamic-pricing/surge-status`);
      setStatus(response.data);
      if (onPriceInfo) onPriceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch surge status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !status) return null;

  const getIcon = () => {
    switch (status.icon) {
      case 'flame': return <Flame className="w-4 h-4" />;
      case 'trending-up': return <TrendingUp className="w-4 h-4" />;
      case 'moon': return <Moon className="w-4 h-4" />;
      case 'tag': return <Tag className="w-4 h-4" />;
      default: return <Check className="w-4 h-4" />;
    }
  };

  const getBadgeClass = () => {
    if (status.demand_level === 'surge') return 'bg-red-100 text-red-700 border-red-200';
    if (status.demand_level === 'high') return 'bg-orange-100 text-orange-700 border-orange-200';
    if (status.demand_level === 'low') return 'bg-green-100 text-green-700 border-green-200';
    if (status.time_factors?.includes('late_night')) return 'bg-purple-100 text-purple-700 border-purple-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <Badge className={`${getBadgeClass()} flex items-center gap-1 px-3 py-1`}>
      {getIcon()}
      <span>{status.message}</span>
      {status.is_surge && (
        <span className="ml-1 font-bold">+{Math.round((status.multiplier - 1) * 100)}%</span>
      )}
    </Badge>
  );
}

// Dynamic Price Display - Shows price with breakdown
export function DynamicPriceDisplay({ serviceType, showBreakdown = false }) {
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (serviceType) {
      fetchPrice();
    }
  }, [serviceType]);

  const fetchPrice = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dynamic-pricing/calculate/${serviceType}?include_ai=true`);
      setPriceData(response.data);
    } catch (error) {
      console.error('Failed to fetch dynamic price:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Calculating best price...</span>
      </div>
    );
  }

  if (!priceData) return null;

  const { base_price, final_price, price_change_percent, is_surge, is_discount, breakdown } = priceData;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-3xl font-bold text-gray-900">
          ${final_price.toFixed(2)}
        </span>
        
        {price_change_percent !== 0 && (
          <div className={`flex items-center gap-1 ${is_surge ? 'text-red-600' : 'text-green-600'}`}>
            {is_surge ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
            <span className="font-medium">
              {price_change_percent > 0 ? '+' : ''}{price_change_percent}%
            </span>
          </div>
        )}
        
        {is_discount && (
          <Badge className="bg-green-100 text-green-700">
            <Tag className="w-3 h-3 mr-1" />
            Savings
          </Badge>
        )}
        
        {is_surge && (
          <Badge className="bg-red-100 text-red-700">
            <Flame className="w-3 h-3 mr-1" />
            Surge
          </Badge>
        )}
      </div>

      {base_price !== final_price && (
        <p className="text-sm text-gray-500">
          Regular price: <span className="line-through">${base_price.toFixed(2)}</span>
        </p>
      )}

      {showBreakdown && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600"
        >
          {expanded ? 'Hide' : 'Show'} price factors
        </Button>
      )}

      {expanded && breakdown && (
        <Card className="mt-2 bg-gray-50">
          <CardContent className="p-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Base Price</span>
              <span className="font-medium">${base_price.toFixed(2)}</span>
            </div>
            
            {breakdown.time_factors.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Time ({breakdown.time_factors.join(', ')})
                </span>
                <span className={breakdown.time_multiplier > 1 ? 'text-orange-600' : 'text-gray-600'}>
                  ×{breakdown.time_multiplier}
                </span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-gray-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Demand ({breakdown.demand_level})
              </span>
              <span className={breakdown.demand_multiplier > 1 ? 'text-orange-600' : breakdown.demand_multiplier < 1 ? 'text-green-600' : 'text-gray-600'}>
                ×{breakdown.demand_multiplier}
              </span>
            </div>
            
            {breakdown.ai_adjustment !== 1 && (
              <div className="flex justify-between">
                <span className="text-gray-600 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  AI Optimization
                </span>
                <span className={breakdown.ai_adjustment > 1 ? 'text-orange-600' : 'text-green-600'}>
                  ×{breakdown.ai_adjustment}
                </span>
              </div>
            )}
            
            {breakdown.ai_reason && breakdown.ai_confidence > 0 && (
              <p className="text-xs text-gray-500 italic pt-2 border-t">
                AI: {breakdown.ai_reason} ({breakdown.ai_confidence}% confidence)
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// All Services Pricing Grid
export function AllServicesPricing({ onServiceSelect }) {
  const [prices, setPrices] = useState({});
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllPrices();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAllPrices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllPrices = async () => {
    try {
      const response = await axios.get(`${API}/dynamic-pricing/all-services`);
      setPrices(response.data.prices);
      setConditions(response.data.current_conditions);
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const SERVICE_NAMES = {
    tow: 'Towing',
    jump_start: 'Jump Start',
    flat_tire: 'Flat Tire',
    lockout: 'Lockout',
    fuel_delivery: 'Fuel Delivery',
    mobile_repair: 'Mobile Repair',
    diagnostic: 'Diagnostic',
    oil_change: 'Oil Change',
    brake_service: 'Brake Service',
    battery_replacement: 'Battery Replace',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-500">Loading prices...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Conditions Banner */}
      {conditions && (
        <div className={`rounded-lg p-3 flex items-center justify-between ${
          conditions.demand_level === 'surge' ? 'bg-red-50 border border-red-200' :
          conditions.demand_level === 'high' ? 'bg-orange-50 border border-orange-200' :
          conditions.demand_level === 'low' ? 'bg-green-50 border border-green-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center gap-2">
            {conditions.demand_level === 'surge' && <Flame className="w-5 h-5 text-red-500" />}
            {conditions.demand_level === 'high' && <TrendingUp className="w-5 h-5 text-orange-500" />}
            {conditions.demand_level === 'low' && <Tag className="w-5 h-5 text-green-500" />}
            {conditions.demand_level === 'normal' && <Check className="w-5 h-5 text-blue-500" />}
            <span className="font-medium capitalize">{conditions.demand_level} Demand</span>
            {conditions.time_factors.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {conditions.time_factors.join(', ')}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchAllPrices}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Prices Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(prices).map(([service, data]) => (
          <Card 
            key={service}
            className={`cursor-pointer hover:shadow-md transition-shadow ${
              data.is_surge ? 'border-red-200 bg-red-50' :
              data.is_discount ? 'border-green-200 bg-green-50' :
              'border-gray-200'
            }`}
            onClick={() => onServiceSelect && onServiceSelect(service, data)}
          >
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">
                {SERVICE_NAMES[service] || service}
              </p>
              <p className="text-xl font-bold text-gray-900">
                ${data.final_price.toFixed(0)}
              </p>
              {data.price_change_percent !== 0 && (
                <p className={`text-xs ${data.is_surge ? 'text-red-600' : 'text-green-600'}`}>
                  {data.price_change_percent > 0 ? '+' : ''}{data.price_change_percent}%
                </p>
              )}
              {data.is_surge && (
                <Flame className="w-4 h-4 text-red-500 mx-auto mt-1" />
              )}
              {data.is_discount && (
                <Tag className="w-4 h-4 text-green-500 mx-auto mt-1" />
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Admin AI Optimization Panel
export function AIPricingOptimizer({ onOptimize }) {
  const [optimizing, setOptimizing] = useState(false);
  const [results, setResults] = useState(null);

  const runOptimization = async () => {
    setOptimizing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/dynamic-pricing/ai-optimize`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setResults(response.data);
      if (onOptimize) onOptimize(response.data);
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              AI Price Optimization
            </h3>
            <p className="text-sm text-gray-500">
              Analyze market conditions and optimize all service prices
            </p>
          </div>
          <Button
            onClick={runOptimization}
            disabled={optimizing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {optimizing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Optimizing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Run AI Optimization
              </>
            )}
          </Button>
        </div>

        {results && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-green-600">
              Optimized {results.optimized_count} services
            </p>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {results.results.map((result, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                  <span className="capitalize">{result.service_type.replace('_', ' ')}</span>
                  <div className="text-right">
                    <span className="font-medium">${result.recommended_price.toFixed(2)}</span>
                    <span className="text-gray-500 ml-2 text-xs">
                      ({result.confidence}% confidence)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DynamicPriceDisplay;
